import React from "react";
import { FormControlLabel, Stack, Switch, Typography } from "@mui/material";
import { RadioOptionsInput } from "../RadioOptionsInput";
import {
  AI_SPEND_INCLUSION_TYPES,
  EXPORT_FIELDS_INCLUSION_TYPES,
  type ExportFieldConfiguration,
} from "./types";
import { FieldConfigurationSelector } from "./FieldConfigurationSelector";
import { firmographicsExportFields } from "./exportConfigurationFields";
import { useAiSpendExportFields } from "./hooks/useAiSpendExportFields";

interface Props {
  inclusion: AI_SPEND_INCLUSION_TYPES;
  setInclusion: (value: AI_SPEND_INCLUSION_TYPES) => void;
  absoluteFieldConfiguration: ExportFieldConfiguration;
  setAbsoluteFieldConfiguration: (cfg: ExportFieldConfiguration) => void;
  relativeFieldConfiguration: ExportFieldConfiguration;
  setRelativeFieldConfiguration: (cfg: ExportFieldConfiguration) => void;
  includeSMB: boolean;
  setIncludeSMB: (value: boolean) => void;
}

const defaultFieldConfig = (): ExportFieldConfiguration => ({
  inclusionType: EXPORT_FIELDS_INCLUSION_TYPES.ALL,
  fields: [],
});

export const AISpendExportOptions: React.FC<Props> = ({
  inclusion,
  setInclusion,
  absoluteFieldConfiguration,
  setAbsoluteFieldConfiguration,
  relativeFieldConfiguration,
  setRelativeFieldConfiguration,
  includeSMB,
  setIncludeSMB,
}) => {
  const { fields: aiSpendExportFields } = useAiSpendExportFields();
  const showAbsolute =
    inclusion === AI_SPEND_INCLUSION_TYPES.ABSOLUTE ||
    inclusion === AI_SPEND_INCLUSION_TYPES.BOTH;
  const showRelative =
    inclusion === AI_SPEND_INCLUSION_TYPES.RELATIVE ||
    inclusion === AI_SPEND_INCLUSION_TYPES.BOTH;

  return (
    <Stack spacing={2} sx={{ width: "70%", paddingTop: "40px" }}>
      <Typography variant="h5" fontWeight={500} fontSize={16}>
        AI Spend
      </Typography>
      <Typography variant="subtitle1" fontWeight="bold">
        What AI Spend data would you like to see in the export?
      </Typography>
      <RadioOptionsInput
        baseId="ai-spend-inclusion"
        value={inclusion}
        options={[
          {
            value: AI_SPEND_INCLUSION_TYPES.NONE,
            label: "Do not include any AI Spend Data",
          },
          {
            value: AI_SPEND_INCLUSION_TYPES.ABSOLUTE,
            label: "Include only rows with Absolute AI Spend Data",
          },
          {
            value: AI_SPEND_INCLUSION_TYPES.RELATIVE,
            label: "Include only rows with Relative AI Spend Data",
          },
          {
            value: AI_SPEND_INCLUSION_TYPES.BOTH,
            label: "Include both Absolute / Relative AI Spend Data",
          },
        ]}
        onChange={(value) =>
          setInclusion(
            AI_SPEND_INCLUSION_TYPES[
              value as keyof typeof AI_SPEND_INCLUSION_TYPES
            ]
          )
        }
      />
      {showAbsolute && (
        <FieldConfigurationSelector
          fields={aiSpendExportFields}
          firmographicsFields={firmographicsExportFields}
          value={absoluteFieldConfiguration ?? defaultFieldConfig()}
          onChange={setAbsoluteFieldConfiguration}
          title="Absolute AI Spend Fields"
          checkboxLabel="Export All AI Spend Data Fields"
        />
      )}
      {showRelative && (
        <FieldConfigurationSelector
          fields={aiSpendExportFields}
          firmographicsFields={firmographicsExportFields}
          value={relativeFieldConfiguration ?? defaultFieldConfig()}
          onChange={setRelativeFieldConfiguration}
          title="Relative AI Spend Fields"
          checkboxLabel="Export All AI Relative Spend Data Fields"
        />
      )}
      <Typography variant="caption" color="text.secondary">
        Note: Spend Tier will be included in your export for proper spend tier
        identification.
      </Typography>
      <FormControlLabel
        control={
          <Switch
            checked={includeSMB}
            onChange={(e) => setIncludeSMB(e.target.checked)}
            disabled={!showAbsolute && !showRelative}
            data-testid="ai-spend-smb-toggle"
          />
        }
        label="Include SMB data"
      />
    </Stack>
  );
};
